import React, { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useAppSelector } from "../../store/hooks";
import { useGetPendingArtworksQuery } from "../../services/rtkApi/artistsGalleryApi";

function roleHas(role: string | null | undefined, target: string): boolean {
  if (!role) return false;
  return role
    .split(",")
    .map((r) => r.trim())
    .includes(target);
}

/** Admin-only: polls pending contributor artworks and toasts when the queue grows or exists on login. */
const ContributorPendingAdminNotifier: React.FC = () => {
  const token = useAppSelector((s) => s.auth.token);
  const bootstrapStatus = useAppSelector((s) => s.auth.bootstrapStatus);
  const role = useAppSelector((s) => s.auth.role);
  const isAdmin = roleHas(role, "admin");
  const ready = bootstrapStatus === "ready" && !!token && isAdmin;

  const { data: pending } = useGetPendingArtworksQuery(undefined, {
    skip: !ready,
    pollingInterval: ready ? 90_000 : 0,
  });

  const prevCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!ready) {
      prevCountRef.current = null;
      return;
    }
    if (pending === undefined) return;

    const n = pending.length;
    const prev = prevCountRef.current;

    if (prev === null) {
      prevCountRef.current = n;
      if (n > 0) {
        toast.info(
          `${n} contributor artwork${n === 1 ? "" : "s"} awaiting review.`,
          {
            toastId: "contributor-queue-initial-pending",
            autoClose: 8000,
          }
        );
      }
      return;
    }

    if (n > prev) {
      const delta = n - prev;
      toast.info(
        `${delta} new submission${delta === 1 ? "" : "s"} in the contributor queue (${n} total).`,
        {
          toastId: `contributor-queue-bump-${n}`,
          autoClose: 6500,
        }
      );
    }

    prevCountRef.current = n;
  }, [ready, pending]);

  return null;
};

export default ContributorPendingAdminNotifier;
